import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, PartyPopper, Building2 } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

function getPasswordStrength(pw) {
  if (!pw) return null;
  const hasUpper = /[A-Z]/.test(pw);
  const hasNumOrSym = /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw);
  if (pw.length < 8 || pw === pw.toLowerCase()) return { level: 'weak', label: 'Svakt', color: 'bg-red-500', width: 'w-1/3' };
  if (pw.length >= 8 && !(hasUpper && hasNumOrSym)) return { level: 'fair', label: 'Middels', color: 'bg-yellow-500', width: 'w-2/3' };
  return { level: 'strong', label: 'Sterkt', color: 'bg-green-500', width: 'w-full' };
}

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { login } = useAuth();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Invite metadata pre-fetched from token
  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(!!token);

  useEffect(() => {
    if (!token) { setMetaLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/auth/invite-info?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setMeta(data);
        if (data?.name && !name) setName(data.name);
      } catch (err) {
        // Non-blocking: still allow form even if metadata fetch fails
        if (!cancelled) setMeta(null);
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Invitasjonen er ugyldig eller utløpt');
      return;
    }
    if (password.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }
    if (password !== confirm) {
      setError('Passordene er ikke like');
      return;
    }
    if (!name.trim()) {
      setError('Vennligst fyll inn navnet ditt');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/accept-invite', { token, name: name.trim(), password });
      setSuccess('Velkommen ombord! 🎉 Logger deg inn...');

      // Try auto-login if backend returned an email; otherwise fallback to /login
      const inviteEmail = data?.email || meta?.email;
      if (inviteEmail) {
        try {
          await login(inviteEmail, password);
          setTimeout(() => navigate('/', { replace: true }), 1200);
          return;
        } catch (_) {
          // Auto-login failed (e.g. backend doesn't accept yet) — fall through to manual login.
        }
      }
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatApiError(detail) || 'Invitasjonen er ugyldig eller utløpt';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative bg-navy-900" data-testid="accept-invite-page">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      <div className="w-full flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="logo" className="w-9 h-9 rounded-lg object-cover" />
            <span className="font-heading text-xl font-bold text-white tracking-tight">AI Resepsjonen</span>
          </div>

          {/* Welcome banner */}
          <div className="bg-gradient-to-br from-accent-blue/15 to-accent-green/10 border border-accent-blue/30 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-accent-blue/20 flex items-center justify-center">
                <PartyPopper size={20} className="text-accent-blue" />
              </div>
              <h2 className="font-heading text-lg font-bold text-white tracking-tight">Velkommen til AI Resepsjonen 🎉</h2>
            </div>
            {metaLoading ? (
              <div className="h-4 w-40 bg-navy-800 rounded animate-pulse" />
            ) : meta?.company_name ? (
              <p className="text-sm text-slate-300 flex items-center gap-1.5">
                <Building2 size={13} className="text-slate-500" /> Du er invitert til <span className="font-semibold text-white">{meta.company_name}</span>
              </p>
            ) : (
              <p className="text-sm text-slate-400">Sett et passord nedenfor for å aktivere kontoen.</p>
            )}
          </div>

          <h3 className="font-heading text-xl font-bold text-white tracking-tight mb-2">Aktiver kontoen din</h3>
          <p className="text-slate-500 mb-6 text-sm">Sett et passord for å fullføre registreringen.</p>

          {error && (
            <div className="bg-accent-red/10 border border-accent-red/20 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm" data-testid="accept-invite-error">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-accent-green/10 border border-accent-green/20 text-accent-green px-4 py-3 rounded-lg mb-6 text-sm" data-testid="accept-invite-success">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Navn</label>
              <input
                data-testid="accept-invite-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors"
                placeholder="Fullt navn"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Passord</label>
              <div className="relative">
                <input
                  data-testid="accept-invite-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors pr-12"
                  placeholder="Minst 8 tegn"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password && (() => {
                const strength = getPasswordStrength(password);
                return (
                  <div className="mt-2">
                    <div className="h-1.5 bg-navy-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`} />
                    </div>
                    <p className={`text-xs mt-1 ${
                      strength.level === 'weak' ? 'text-red-400' :
                      strength.level === 'fair' ? 'text-yellow-400' : 'text-green-400'
                    }`}>{strength.label} passord</p>
                  </div>
                );
              })()}
            </div>

            <div>
              <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bekreft passord</label>
              <input
                data-testid="accept-invite-confirm"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-colors"
                placeholder="Skriv passordet på nytt"
                required
                minLength={8}
              />
            </div>

            <button
              data-testid="accept-invite-submit"
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Oppretter konto...' : 'Aktiver konto'}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-xs text-accent-blue hover:text-accent-blue-hover transition-colors">
                Tilbake til innlogging
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
